// src/hooks/useAuth.jsx
import { useState, useEffect, useCallback } from "react";
import { login as apiLogin, register as apiRegister, setAuthToken } from "../utils/api";
import { extractErrorMessage } from "../utils/errorMessages";

export function useAuth() {
  // Use "jwt_token" in localStorage so NavBar sees it consistently
  const [token, setToken] = useState(localStorage.getItem("jwt_token") || "");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Whenever token changes, update default Axios header and user state
  useEffect(() => {
    if (token) {
      setAuthToken(token);
      // If we have a token, create a user object
      // In a real app, you might want to decode the JWT or make an API call
      setUser({ uid: 'user-id' }); // You might want to decode the JWT to get the real user ID
    } else {
      setUser(null);
    }
    // Set loading to false after initial auth check is complete
    setLoading(false);
  }, [token]);

  // Refresh user with token
  const refreshUserWithToken = useCallback(async (newToken) => {
    setToken(newToken);
  }, []);

  // Authenticate: either "login" or "register"
  const authenticate = useCallback(async (creds, mode) => {
    setLoading(true);
    try {
      const fn = mode === "login" ? apiLogin : apiRegister;
      const { data } = await fn(creds);
      // Store under "jwt_token"
      const newToken = data.idToken;
      localStorage.setItem("jwt_token", newToken);
      setToken(newToken);
      return { success: true, error: null };
    } catch (error) {
      console.error(`Authentication error (${mode}):`, error);
      const errorMessage = extractErrorMessage(error);
      return { success: false, error: errorMessage, originalError: error };
    } finally {
      setLoading(false);
    }
  }, []);

  // Logout: clear everything
  const logout = useCallback(() => {
    localStorage.removeItem("jwt_token");
    setToken("");
    setUser(null);
  }, []);

  return { user, token, loading, authenticate, logout, refreshUserWithToken };
}
